import { FormEvent, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toast } from "react-toastify";

import { formatDateTime } from "~/libs";

import {
  useApisMembersEmployeeConditionSurveysShow,
  useApiMembersEmployeeConditionSubmissionsCreate,
  useArray,
} from "~/hooks";

import { MyRoutes } from "~/config/Paths";

import {
  CheckBoxType,
  EmployeeConditionSurveyWithQuestionsType,
  FormEmployeeConditionSubmissionAnswerType,
  RadioButtonType,
  SelectBoxType,
  TextAreaType,
  TextFieldType,
} from "~/domains";

type ReturnType = {
  employeeConditionSurvey: EmployeeConditionSurveyWithQuestionsType | null;
  isError: boolean;
  isCreating: boolean;
  postDate: Date;
  onChangePostDate: (date: Date) => void;
  onChangeAnswer: (value: string | undefined, questionId: string) => void;
  answers: FormEmployeeConditionSubmissionAnswerType[];
  handleSubmit: (e: FormEvent<HTMLFormElement>) => void;
};

type HandleChangeAnswerPropsType = {
  answer: FormEmployeeConditionSubmissionAnswerType | undefined;
  questionId: string;
  value: string | undefined;
  label?: string;
};

type PropsType = {
  employeeConditionId: string;
};

export function useEmployeeConditionSubmissionForm({
  employeeConditionId,
}: PropsType): ReturnType {
  const navigate = useNavigate();
  const [employeeConditionSurvey, setEmployeeConditionSurvey] =
    useState<EmployeeConditionSurveyWithQuestionsType | null>(null);
  const [postDate, setPostDate] = useState<Date>(new Date());
  const { items: answers, setItems: setAnswers } =
    useArray<FormEmployeeConditionSubmissionAnswerType>([]);
  const { mutate: createEmployeeConditionSubmission, isLoading: isCreating } =
    useApiMembersEmployeeConditionSubmissionsCreate();

  const { isError } = useApisMembersEmployeeConditionSurveysShow({
    id: employeeConditionId,
    config: {
      enabled: !!employeeConditionId,
      onSuccess: (data) => {
        setEmployeeConditionSurvey(data.employeeConditionSurvey);
      },
    },
  });

  // テキストフィールドまたはテキストエリアの変更を処理する関数
  const handleChangeText = ({
    answer,
    questionId,
    value,
  }: HandleChangeAnswerPropsType) => {
    if (answer) {
      setAnswers(
        answers.map((a) =>
          a.employeeConditionSurveyQuestionId === questionId
            ? { ...a, answer: value }
            : a,
        ),
      );
    } else {
      setAnswers([
        ...answers,
        {
          employeeConditionSurveyQuestionId: questionId,
          answer: value,
          employeeConditionSurveyQuestionOptionId: "",
        },
      ]);
    }
  };

  // ラジオボタンの変更を処理する関数
  const handleChangeRadioButton = ({
    answer,
    questionId,
    value,
    label,
  }: HandleChangeAnswerPropsType) => {
    if (value === undefined) {
      setAnswers(
        answers.filter(
          (a) => a.employeeConditionSurveyQuestionId !== questionId,
        ),
      );
      return;
    }
    if (answer) {
      // 既存の回答がある場合、選択肢IDと回答を更新する
      setAnswers(
        answers.map((a) =>
          a.employeeConditionSurveyQuestionId === questionId
            ? {
                ...a,
                employeeConditionSurveyQuestionOptionId: value,
                answer: value,
              }
            : a,
        ),
      );
    } else {
      setAnswers([
        ...answers,
        {
          employeeConditionSurveyQuestionId: questionId,
          answer: label,
          employeeConditionSurveyQuestionOptionId: value,
        },
      ]);
    }
  };

  // チェックボックスの変更を処理する関数
  const handleChangeCheckBox = ({
    answer,
    questionId,
    value,
    label,
  }: HandleChangeAnswerPropsType) => {
    if (answer && answer.employeeConditionSurveyQuestionOptionId === value) {
      // 既存の回答があり、選択肢IDが一致する場合、回答を削除する
      setAnswers(
        answers.filter(
          (a) =>
            !(
              a.employeeConditionSurveyQuestionId === questionId &&
              a.employeeConditionSurveyQuestionOptionId === value
            ),
        ),
      );
    } else {
      setAnswers([
        ...answers,
        {
          employeeConditionSurveyQuestionId: questionId,
          answer: label,
          employeeConditionSurveyQuestionOptionId: value,
        },
      ]);
    }
  };

  // 質問に対する回答の変更を処理するメイン関数
  const onChangeAnswer = (value: string | undefined, questionId: string) => {
    // 該当する質問を見つける
    const question = employeeConditionSurvey?.questions.find(
      (q) => q.id === questionId,
    );

    // 質問が見つからない場合は処理を終了する
    if (!question) return;

    // 選択されたオプションのラベルを見つける
    const label = question.options?.find((q) => q.id === value)?.option;

    // 既存の回答を見つける
    // チェックボックスの場合は選択肢IDが一致する回答を見つける

    const answer =
      question.questionType.type === CheckBoxType.type
        ? answers.find(
            (a) =>
              a.employeeConditionSurveyQuestionId === questionId &&
              a.employeeConditionSurveyQuestionOptionId === value,
          )
        : answers.find(
            (a) => a.employeeConditionSurveyQuestionId === questionId,
          );

    // 質問のタイプに応じて対応する変更関数を呼び出す
    if (
      question.questionType.type === TextFieldType.type ||
      question.questionType.type === TextAreaType.type
    ) {
      handleChangeText({ answer, questionId, value });
    } else if (
      question.questionType.type === RadioButtonType.type ||
      question.questionType.type === SelectBoxType.type
    ) {
      handleChangeRadioButton({ answer, questionId, value, label });
    } else if (question.questionType.type === CheckBoxType.type) {
      handleChangeCheckBox({ answer, questionId, value, label });
    }
  };

  const handleCreateEmployeeConditionSubmission = (
    e: FormEvent<HTMLFormElement>,
  ) => {
    e.preventDefault();

    createEmployeeConditionSubmission(
      {
        employeeConditionSurveyId: employeeConditionId,
        body: {
          postDate: formatDateTime(postDate, "yyyy-MM-dd"),
          employeeConditionSubmissionAnswers: answers.map((a) => {
            return {
              employeeConditionSurveyQuestionId:
                a.employeeConditionSurveyQuestionId,
              employeeConditionSurveyQuestionOptionId:
                a.employeeConditionSurveyQuestionOptionId,
              answer: a.answer,
            };
          }),
        },
      },
      {
        onSuccess: (data) => {
          toast(data.message);
          navigate(MyRoutes.Members.employeeConditionSubmissionsIndex());
        },
      },
    );
  };

  return {
    handleSubmit: handleCreateEmployeeConditionSubmission,
    isError,
    isCreating,
    employeeConditionSurvey,
    onChangeAnswer,
    answers,
    postDate,
    onChangePostDate: setPostDate,
  };
}
